import React, { createRef, useEffect, useState } from 'react'
import { LicenseFormActions, licenseFormProps, LicenseFeatureVersion } from './LicenseForm.types'
import { Button, ButtonType, IToastNotification, Select, Switch, ToasterContainer, ToasterMessageType } from '@digicert/dcone-common-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { Translate } from 'react-localize-redux'
import cn from 'classnames'
import { Tooltip } from 'antd'
import styles from './LicenseForm.module.scss'
import { translate } from 'shared/helpers/utils'
import HTTPUrlHelper from 'shared/helpers/HTTPUrlHelper'
import { Applications, IlicenseCard } from '../Tenant.types'
import { UNLIMITED } from 'core/constants'
import { IAccessScope } from 'reducers/Reducers.interfaces'

declare global {
  interface Window {
    licenseData: any
  }
}

export const LicenseForm = (props: licenseFormProps) => {
  const { dataSource, onCancelClick, updateLicenseByApp, createLicenseAllocationUpdateEvent, context, id, license, app_code, partnerAccountId, type, licenseFeatureVersion, showToggle } = props

  const getDataSource = () => {
    if (licenseFeatureVersion) {
      return dataSource.filter(license => license?.version === licenseFeatureVersion)
    }
    return dataSource
  }
  const dataSourceState = getDataSource()
  const accountRemainingArray = new Array<string>(dataSourceState.length).fill('-')
  const licenseRemainingArray = new Array<string>(dataSourceState.length).fill('-')
  const updateLimitArray = new Array<string>(dataSourceState.length)
  const licenseResetAllowedApps = ['signingmanager']
  const isLicenseResetAllowed = licenseResetAllowedApps.some((app) => props?.context?.includes(app));
  const actions = [
    {
      label: translate('common.noChange') as string,
      value: LicenseFormActions.NO_CHANGE
    },
    {
      label: translate('common.add') as string,
      value: LicenseFormActions.ADD
    },
    {
      label: translate('common.subtract') as string,
      value: LicenseFormActions.SUBTRACT
    }
  ]
  const defaultErrorMessages = new Array<string>(dataSourceState.length).fill('')
  const errorRefs = new Array<any>(dataSourceState.length)
  const hsmKeyPairFeatureId = 'hsm_keypair_units';

  for (const i in dataSourceState) {
    errorRefs[i] = createRef<HTMLParagraphElement>()
  }

  const [errorMessages, setErrorMessages] = useState(defaultErrorMessages)
  const [submitDisabled, setSubmitDisabled] = useState(true)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [licenseData, setLicenseData] = useState(dataSourceState)
  const [accountRemaining, setAccountRemaining] = useState(accountRemainingArray)
  const [licenseRemaining, setLicenseRemaining] = useState(licenseRemainingArray)
  const [updateLimit, setUpdateLimit] = useState(updateLimitArray)
  const [action, setAction] = useState(new Array<string>(dataSourceState.length).fill('no-change'))
  const [inputValues, setInputValues] = useState(new Array<string>(dataSourceState.length).fill(''))
  const [selectedLicenseVersion, setSelectedLicenseVersion] = useState<LicenseFeatureVersion>(LicenseFeatureVersion.V1)
  const [parentLicenseData, setParentLicenseData] = useState(dataSource)
  const [v2Enabled, setV2Enabled] = useState(false)
  const [notifications, setNotifications] = useState<IToastNotification[]>([])
  const [platformLicense, setPlatformLicense] = useState<any>([])
  let inputRefs: Array<HTMLInputElement> = [];

  useEffect(() => {
    if (app_code) {
      if (partnerAccountId) {
        fetch(HTTPUrlHelper.getAccountLicenseByApp(app_code, partnerAccountId)).then(async (response: any) => {
          setupStates(response)
        })
      } else {
        fetch(HTTPUrlHelper.getAMLicenseByApp(app_code)).then(async (response: any) => {
          setupStates(response)
        })
      }
    }
  }, [app_code])

  const setupStates = async (response) => {
    if (response.ok) {
      const licenseFeature = await response.json()
      setPlatformLicense(licenseFeature)
      const data = licenseData.map((data, index) => {
        updateLimitArray[index] = data.totalAllocated.toString()
        if (licenseFeature) {
          const feature = licenseFeature.features.find(feat => feat.name.toLowerCase() === data.featureId.toLowerCase())
          if (feature) {
            data.licenseAllocation = feature.attributes.remaining === -1 ? translate('common.form.unlimited') : feature.attributes.remaining
          } else {
            data.licenseAllocation = '-'
          }
        }
        return data
      })
      setUpdateLimit(updateLimitArray)
      if (licenseFeatureVersion) {
        const licenseDataState = data.filter(license => license?.version === licenseFeatureVersion)
        setLicenseData(licenseDataState)
        setSelectedLicenseVersion(licenseFeatureVersion)
      } else {
        setLicenseData(data)
      }
    }
  }

  let licenseFeatures
  if (license) {
    license.applications.map(app => {
      if (app.app_code === app_code) {
        licenseFeatures = app.features
      }
    })
  }

  const checkValidation = (input: string, item: IlicenseCard, action: string) => {
    const licenseFeatureLimit = licenseFeatures.find(licenseFeature => licenseFeature.name === item.featureId).attributes.limit
    let validation = ''

    if (
      !/^\d*$/.test(input) ||
      /^0[0-9].*$/.test(input) ||
      /^\d*\.\d*$/.test(input) ||
      /^([a-zA-Z])\w+/.test(input)
    ) {
      validation = translate('dashboardLicense.validation.positiveNumber') as string
    }

    if (/^\d*$/.test(input)) {
      if (
        action === LicenseFormActions.ADD && licenseFeatureLimit !== -1 && parseInt(item.used, 10) < 0 && parseInt(input, 10) < parseInt(item.used, 10)
      ) {
        validation = translate('dashboardLicense.validation.range') + ` ${parseInt(item.used, 10)}` + ' ' + translate('common.to') + ' ' + ` ${parseInt(item.licenseAllocation, 10)}.`
      } else if (action === LicenseFormActions.ADD && item.licenseAllocation !== '-' && item.licenseAllocation !== '0' && parseInt(input, 10) > parseInt(item.licenseAllocation, 10)) {
        validation = translate('dashboardLicense.validation.range') + ' 1 ' + translate('common.to') + ' ' + ` ${parseInt(item.licenseAllocation, 10)}.`
      }
      else if (action === LicenseFormActions.SUBTRACT && item.licenseAllocation !== '-' && item.licenseAllocation !== '0' && parseInt(input, 10) > item.totalAllocated) {
        validation = translate('dashboardLicense.validation.range') + ' 1 ' + translate('common.to') + ' ' + ` ${item.totalAllocated}.`
      }
      else if (action === LicenseFormActions.RESET && item.licenseAllocation !== '-' && item.licenseAllocation !== '0' && parseInt(input, 10) > item.totalAllocated + parseInt(item.licenseAllocation, 10)) {
        validation = translate('dashboardLicense.validation.range') + ' 0 ' + translate('common.to') + ' ' + ` ${item.licenseAllocation + item.totalAllocated}.`
      }
    }
    if (action === LicenseFormActions.SUBTRACT && parseInt(input, 10) > (item.totalAllocated - parseInt(item.used, 10))) {
      validation = translate('dashboardLicense.validation.cannotExceedNumber') + ` ${item.totalAllocated - parseInt(item.used, 10)}.`
    }

    return validation
  }

  const changeTotal = (
    value: string,
    index: number,
    action: string) => {
    const licenseTemp = [...licenseRemaining]
    const accountTemp = [...accountRemaining]
    const updateLimitTemp = [...updateLimit]
    if (action === LicenseFormActions.ADD && typeof parseInt(value, 10) === 'number') {
      licenseTemp[index] = licenseData[index].licenseAllocation !== UNLIMITED ? (parseInt(licenseData[index].licenseAllocation, 10) - parseInt(value, 10)).toString() : licenseData[index].licenseAllocation
      accountTemp[index] = (licenseData[index].remaining + parseInt(value, 10)).toString()
      updateLimitTemp[index] = (licenseData[index].totalAllocated + parseInt(value, 10)).toString()
    }
    if (action === LicenseFormActions.SUBTRACT && typeof parseInt(value, 10) === 'number') {
      licenseTemp[index] = licenseData[index].licenseAllocation !== UNLIMITED ? (parseInt(licenseData[index].licenseAllocation, 10) + parseInt(value, 10)).toString() : licenseData[index].licenseAllocation
      accountTemp[index] = (licenseData[index].remaining - parseInt(value, 10)).toString()
      updateLimitTemp[index] = (licenseData[index].totalAllocated - parseInt(value, 10)).toString()
    }
    if (action === LicenseFormActions.RESET && typeof parseInt(value, 10) === 'number') {
      licenseTemp[index] = licenseData[index].licenseAllocation !== UNLIMITED ? (parseInt(licenseData[index].licenseAllocation, 10) + licenseData[index].totalAllocated - parseInt(value, 10)).toString() : licenseData[index].licenseAllocation
      accountTemp[index] = (parseInt(value, 10)).toString()
      updateLimitTemp[index] = (parseInt(value, 10)).toString()
    }
    if (!value) {
      accountTemp[index] = '-'
      licenseTemp[index] = '-'
      updateLimitTemp[index] = licenseData[index].totalAllocated.toString()
    }
    setLicenseRemaining(licenseTemp)
    setAccountRemaining(accountTemp)
    setUpdateLimit(updateLimitTemp)
  }

  const changeLicense = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    item: IlicenseCard
  ) => {
    const inputValueArray = [...inputValues]
    inputValueArray[index] = event.target.value
    setInputValues(inputValueArray)
    changeTotal(event.target.value, index, action[index])
    const ref = errorRefs[index].current
    if (ref) {
      ref.style.display = 'none'

      const validation = checkValidation(
        event.target.value,
        item, action[index]
      )

      if (validation) {
        setSubmitDisabled(true)
        ref.style.display = 'block'
      }

      const tempErrorMessage = [...errorMessages]
      tempErrorMessage[index] = validation
      setErrorMessages(tempErrorMessage)

      setSubmitDisabled(false)
      for (const item of tempErrorMessage) {
        if (item !== '') {
          setSubmitDisabled(true)
        }
      }
      if (action.every(a => a === 'no-change') && tempErrorMessage.every((error) => error !== '')) {
        setSubmitDisabled(true)
      }

      if (action.every(a => a !== 'no-change') && tempErrorMessage.every((error) => error === '')) {
        setSubmitDisabled(false)
      }
    }
  }

  const onCancel = () => {
    onCancelClick(null, {})
  }
  const getQuantity = (action, index) => {
    switch (action) {
      case LicenseFormActions.ADD:
        return parseInt(inputValues[index - 1], 10)
      case LicenseFormActions.SUBTRACT:
        return -parseInt(inputValues[index - 1], 10)
      case LicenseFormActions.RESET:
        return parseInt(inputValues[index - 1], 10) - licenseData[index - 1].remaining
      default:
        return inputValues.map(() => 0)
    }
  }

  const onSubmit = () => {
    let index = 0
    const payload: any = {
      features: licenseData.map(feature => {
        index++
        return {
          name: feature.featureId,
          attributes: {
            limit: parseInt(updateLimit[index - 1], 10)
          },
          reset: action[index - 1] === LicenseFormActions.RESET,
          old_limit: feature.totalAllocated
        }
      })
    }
    payload.license_feature_version = selectedLicenseVersion;

    index = 0
    const licenseEventPayload = {
      features: licenseData.map(feature => {
        index++
        if (inputValues[index - 1] != '') {
          return {
            name: feature.featureId,
            quantity: getQuantity(action[index - 1], index),
            updated_limit: accountRemaining[index - 1] !== '-' ? parseInt(accountRemaining[index - 1], 10) : feature.remaining
          }
        }
      }).filter(feat => feat != undefined)
    }
    updateLicenseByApp(payload, context, id, licenseEventPayload, app_code)
      .then((data) => {
        onCancelClick(null, {
          app_code: app_code,
          data: window.licenseData
        })
      })
      .catch((error) => {
        console.error('License update failed:', error)
        setNotifications(
          [
            ...notifications,
            {
              type: ToasterMessageType.ERROR,
              description: translate('notifications.errors.licenseUpdateFailed'),
              id: 'license-update-failed'
            }
          ]
        )
        setSubmitDisabled(false);
      })
  }

  const hideNotification = (id: string | number) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
  }

  function onChangeAction(e, index) {
    const actionArr = [...action]
    actionArr[index] = e
    setAction(actionArr)
    const inputArray = [...inputValues]
    if (e === 'no-change') {
      inputArray[index] = ''
      setInputValues(inputArray)
      changeTotal('', index, e)
    } else {
      changeTotal(inputValues[index], index, e)
    }
    const isTrue = (currentValue) => currentValue === 'no-change';
    if (actionArr.every(isTrue)) {
      setSubmitDisabled(true)
    }
  }

  const onBtnMouseEnter = () => {
    if (submitDisabled && errorMessages.every((item) => item === '')) {
      setTooltipVisible(true)
    }
  }

  const onBtnMouseLeave = () => {
    setTooltipVisible(false)
  }

  const onSelect = (index) => {
    setTimeout(() => {
      inputRefs[index].focus()
    }, 100)
  }

  const saveInputRef = (el) => {
    if (el) {
      inputRefs.push(el)
    }
  }

  const resetAllLicensesToZero = () => {
    const inputArray = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? '' : '0'
    );

    const actionArray = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? LicenseFormActions.NO_CHANGE : LicenseFormActions.RESET
    );
    const licenseTemp = licenseData.map((data) =>
      data.featureId === hsmKeyPairFeatureId
        ? data.licenseAllocation
        : data.licenseAllocation !== UNLIMITED
          ? (
            parseInt(data.licenseAllocation, 10) +
            data.totalAllocated -
            0 // Reset to zero
          ).toString()
          : data.licenseAllocation
    );

    const accountTemp = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? item.remaining.toString() : '0'
    );

    const updateLimitTemp = dataSource.map((item) =>
      item.featureId === hsmKeyPairFeatureId ? item.totalAllocated.toString() : '0'
    );
    setInputValues(inputArray);
    setAction(actionArray);

    setLicenseRemaining(licenseTemp);
    setAccountRemaining(accountTemp);
    setUpdateLimit(updateLimitTemp);
    setSubmitDisabled(false);
  };

  const manageV2VersionSwitch = (value: boolean) => {
    const version = value ? LicenseFeatureVersion.V2 : LicenseFeatureVersion.V2_5;
    updateLicenseState(version);
  }

  const manageLicenseSwitch = (value: boolean) => {
    setV2Enabled(value)
    const version = value ? LicenseFeatureVersion.V2 : LicenseFeatureVersion.V1;
    updateLicenseState(version);
  }

  const updateLicenseState = (version: LicenseFeatureVersion) => {
    const licenseDataState = parentLicenseData.filter(license => license?.version === version);
    const newAccountRemainingArray = new Array<string>(licenseDataState.length).fill('-');
    const newLicenseRemainingArray = new Array<string>(licenseDataState.length).fill('-');
    const newUpdateLimitArray = new Array<string>(licenseDataState.length);
    const newDefaultErrorMessages = new Array<string>(licenseDataState.length).fill('');

    const licenseDataWithPlatformUpdate = licenseDataState.map((licenseSeat, index) => {
      newUpdateLimitArray[index] = licenseSeat.totalAllocated.toString();
      if (platformLicense) {
        const feature = platformLicense.features?.find(
          feat => feat.name.toLowerCase() === licenseSeat.featureId.toLowerCase()
        );
        if (feature) {
          licenseSeat.licenseAllocation =
            feature.attributes.remaining === -1
              ? translate('common.form.unlimited')
              : feature.attributes.remaining;
        } else {
          licenseSeat.licenseAllocation = '-';
        }
      }
      return licenseSeat;
    });
    setSelectedLicenseVersion(version);

    setLicenseData(licenseDataWithPlatformUpdate);
    setAccountRemaining(newAccountRemainingArray);
    setLicenseRemaining(newLicenseRemainingArray);
    setUpdateLimit(newUpdateLimitArray);
    setAction(new Array<string>(licenseDataState.length).fill('no-change'));
    setInputValues(new Array<string>(licenseDataState.length).fill(''));
    setErrorMessages(newDefaultErrorMessages);
    setSubmitDisabled(newUpdateLimitArray.every((limit) => limit === '0'));

  }

  return (
    <>
      <section className={styles.licenseCardForm}>
        {showToggle && <>
          <p><Translate id='dashboardLicense.switchToNewLicense' /></p>
          <Switch
            className={styles.newLicenseSwitch}
            id='newLicenseSwitch'
            checkedChildren='On'
            unCheckedChildren='Off'
            onChange={manageLicenseSwitch}
            checked={selectedLicenseVersion !== LicenseFeatureVersion.V1}
          /></>}
        {props?.app_code?.includes(Applications.MPKI) && parentLicenseData.filter(license => license?.version === LicenseFeatureVersion.V2_5).length > 0 && (v2Enabled || licenseFeatureVersion === LicenseFeatureVersion.V2_5 || licenseFeatureVersion === LicenseFeatureVersion.V2) && <>
          <p><Translate id='dashboardLicense.switchToFlex' /></p>
          <Switch
            className={styles.newLicenseSwitch}
            id='v2licenseSwitch'
            checkedChildren={'On'}
            unCheckedChildren={'Off'}
            onChange={manageV2VersionSwitch}
            checked={selectedLicenseVersion === LicenseFeatureVersion.V2}
          /></>}
        {isLicenseResetAllowed && type !== IAccessScope.partner &&
          <div className={styles.restAllLicenses}>
            <a id='resetAllLicense' onClick={resetAllLicensesToZero}>
              <Translate id='dashboardLicense.resetAllLicenses' />
            </a>
          </div>}
        <ul className={styles.licenseCardFormHeader}>
          <li><strong><Translate id='common.form.licenseType' /></strong></li>
          <li><Translate id='common.form.updateNumberOfLicenses' /></li>
          <li><Translate id='common.form.remainingAccount' /></li>
          {partnerAccountId ? <li><Translate id='common.form.remainingPartner' /></li> : <li><Translate id='common.form.remainingPlatform' /></li>}
        </ul>
        {licenseData.map((item, index) => {
          let options = [...actions]
          if (parseInt(item.licenseAllocation) === 0 && item.totalAllocated == 0 && item.remaining == 0) {
            options.splice(1, 2)
          }
          else if (item.totalAllocated == parseInt(item.used) && parseInt(item.used) > 0 && parseInt(item.licenseAllocation) == 0) {
            options.splice(1, 2)
          }
          else if (parseInt(item.licenseAllocation) === 0) {
            options.splice(1, 1)
          }
          else if (item.remaining <= 0) {
            options.splice(2, 1)
          }
          else if (typeof item.licenseAllocation == 'undefined' && item.remaining.toString() == '-') {
            options.splice(1, 2)
          }

          if (isLicenseResetAllowed && type !== IAccessScope.partner && typeof item.licenseAllocation !== 'undefined'
            && item.remaining.toString() !== '-' && item.featureId !== hsmKeyPairFeatureId) {
            options.push({
              label: translate('common.reset') as string,
              value: LicenseFormActions.RESET
            })
          }
          return (
            <div key={index} className={styles.licenseCardFormRowContainer}>
              <div className={styles.licenseCardFormRow}>
                <div>{item.licenseType}</div>
              </div>
              <div className={styles.licenseCardFormRow}>
                <div className={styles.licenseCardFormRowItem}>
                  <div className={styles.licenseCardFormRowItemActions}>
                    <Select
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                      defaultValue="no-change"
                      label=""
                      onChange={(event): void => { onChangeAction(event, index) }}
                      options={options}
                      size="s"
                      value={action[index]}
                      onSelect={() => onSelect(index)}
                    />
                    <input
                      ref={saveInputRef}
                      type='text'
                      className={cn(styles.licenseInput, {
                        [styles.invalid]: errorMessages[index] !== ''
                      })}
                      value={inputValues[index]}
                      disabled={action[index] === 'no-change'}
                      onChange={(event): void => {
                        changeLicense(index, event, item)
                      }}
                    />
                  </div>
                  <div className={styles.errors} ref={errorRefs[index]}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    <p className={styles.errorMessage}>{errorMessages[index]}</p>
                  </div>
                </div>
                <div className={styles.licenseCardFormRowItem}>
                  <p><Translate id='common.form.totalAfterUpdate' /></p>
                </div>
              </div>
              <div className={styles.licenseCardFormRow}>
                <div className={styles.licenseCardFormRowItem}>
                  <p>{item.remaining}</p>
                </div>
                <div className={styles.licenseCardFormRowItem}>
                  <p className={parseInt(accountRemaining[index]) > item.remaining ? styles.increased : styles.decreased}>{accountRemaining[index]}</p>
                </div>
              </div>
              <div className={styles.licenseCardFormRow}>
                <div className={styles.licenseCardFormRowItem}>
                  <p>{item.licenseAllocation}</p>
                </div>
                <div className={styles.licenseCardFormRowItem}>
                  <p className={parseInt(licenseRemaining[index]) > item.remaining ? styles.decreased : styles.increased}>{licenseRemaining[index]}</p>
                </div>
              </div>
            </div>
          )
        })
        }
      </section>
      <ToasterContainer hideNotification={hideNotification} notifications={notifications} />
      <div className={styles.buttonWrap}>
        <div className={styles.cancelButton}>
          <Button buttonType={ButtonType.SECONDARY} onClick={onCancel}>
            <Translate id='profile.cancelBtn' />
          </Button>
        </div>
        <div
          onMouseEnter={onBtnMouseEnter}
          onMouseLeave={onBtnMouseLeave}
          style={{ display: 'inline' }}
        >
          <Tooltip
            title={<Translate id='dashboardLicense.tooltip' />}
            placement='top'
            visible={tooltipVisible}
          >
            <button
              className={cn(styles.submitButton, {
                [styles.btnDisabled]: submitDisabled
              })}
              onClick={onSubmit}
              disabled={submitDisabled}
            >
              <Translate id='common.form.buttons.updateLicense' />
            </button>
          </Tooltip>
        </div>
      </div>
    </>
  )
}
